import { createContext, useContext, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const FilterContext = createContext();

export const useFilters = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error("useFilters must be used within a FilterProvider");
  }
  return context;
};

export const FilterProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    make: searchParams.get("make") || "all",
    country: searchParams.get("country") || "all",
    priceRange: [
      parseInt(searchParams.get("minPrice")) || 0,
      parseInt(searchParams.get("maxPrice")) || 2000000000,
    ],
    sortBy: searchParams.get("sortBy") || "id-desc",
    searchQuery: searchParams.get("search") || "",
  });

  // Update filters when URL changes
  useEffect(() => {
    setFilters({
      make: searchParams.get("make") || "all",
      country: searchParams.get("country") || "all",
      priceRange: [
        parseInt(searchParams.get("minPrice")) || 0,
        parseInt(searchParams.get("maxPrice")) || 2000000000,
      ],
      sortBy: searchParams.get("sortBy") || "id-desc",
      searchQuery: searchParams.get("search") || "",
    });
  }, [searchParams]);

  const updateFilters = (newFilters) => {
    setFilters(newFilters);

    // Update URL
    const newParams = new URLSearchParams();

    if (newFilters.make !== "all") newParams.set("make", newFilters.make);
    if (newFilters.country !== "all")
      newParams.set("country", newFilters.country);
    if (newFilters.searchQuery) newParams.set("search", newFilters.searchQuery);
    if (newFilters.priceRange[0] > 0)
      newParams.set("minPrice", newFilters.priceRange[0]);
    if (newFilters.priceRange[1] < 2000000000)
      newParams.set("maxPrice", newFilters.priceRange[1]);
    if (newFilters.sortBy !== "id-desc")
      newParams.set("sortBy", newFilters.sortBy);

    setSearchParams(newParams);
  };

  const clearFilters = () => {
    const defaultFilters = {
      make: "all",
      country: "all",
      priceRange: [0, 2000000000],
      sortBy: "id-desc",
      searchQuery: "",
    };
    updateFilters(defaultFilters);
  };

  return (
    <FilterContext.Provider value={{ filters, updateFilters, clearFilters }}>
      {children}
    </FilterContext.Provider>
  );
};
